import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// ─── Shared internals ────────────────────────────────────────────────────────

function CardMeta({ meta }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "12px", mb: "20px" }}>
            <AccessTimeIcon sx={{ fontSize: "0.85rem", color: "#9CA3AF" }} />
            <Typography
                sx={{
                    color: "#9CA3AF",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                }}
            >
                {meta}
            </Typography>
        </Box>
    );
}

function CardIconBox({ icon: Icon, locked }) {
    return (
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                backgroundColor: locked ? "#F3F4F6" : "#DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <Icon sx={{ color: locked ? "#9CA3AF" : "#3B82F6", fontSize: "1.4rem" }} />
        </Box>
    );
}

function CardShell({ children }) {
    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                padding: "20px",
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 220,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
            }}
        >
            {children}
        </Box>
    );
}

// ─── Badge variants ───────────────────────────────────────────────────────────

function AttemptedBadge({ band }) {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#16A34A",
                borderRadius: "50px",
                padding: "4px 10px",
            }}
        >
            <CheckIcon sx={{ color: "#FFFFFF", fontSize: "0.8rem" }} />
            <Typography
                sx={{
                    color: "#FFFFFF",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    lineHeight: 1,
                }}
            >
                Attempted
            </Typography>
            <Typography
                sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.75rem",
                    lineHeight: 1,
                }}
            >
                |
            </Typography>
            <Typography
                sx={{
                    color: "#FFFFFF",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    lineHeight: 1,
                }}
            >
                Band {band}
            </Typography>
        </Box>
    );
}

function UnattemptedBadge() {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#F59E0B",
                borderRadius: "50px",
                padding: "4px 10px",
            }}
        >
            <AccessTimeIcon sx={{ color: "#FFFFFF", fontSize: "0.8rem" }} />
            <Typography
                sx={{
                    color: "#FFFFFF",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    lineHeight: 1,
                }}
            >
                Unattempted
            </Typography>
        </Box>
    );
}

function LockedBadge() {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#9CA3AF",
                borderRadius: "50px",
                padding: "4px 10px",
            }}
        >
            <LockOutlinedIcon sx={{ color: "#FFFFFF", fontSize: "0.8rem" }} />
            <Typography
                sx={{
                    color: "#FFFFFF",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    lineHeight: 1,
                }}
            >
                Locked
            </Typography>
        </Box>
    );
}

// ─── Card header row: icon + title + badge ────────────────────────────────────

function CardHeader({ icon, title, badge, locked }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <CardIconBox icon={icon} locked={locked} />
                <Typography
                    sx={{
                        color: locked ? "#9CA3AF" : "#111827",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 700,
                        fontSize: "1rem",
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
                {badge}
            </Box>
        </Box>
    );
}

// ─── 4 exported card states ───────────────────────────────────────────────────

/**
 * State 1 — Attempted
 * Shows green badge with band score, blue icon, "View Results" outlined blue button.
 */
export function AttemptedCard({ icon, title, meta, band, onViewResults }) {
    return (
        <CardShell>
            <CardHeader
                icon={icon}
                title={title}
                locked={false}
                badge={<AttemptedBadge band={band} />}
            />
            <CardMeta meta={meta} />
            <Box sx={{ mt: "auto" }}>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onViewResults}
                    sx={{
                        border: "1px solid #3B82F6",
                        color: "#3B82F6",
                        borderRadius: "6px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        fontFamily: "Mulish, sans-serif",
                        padding: "10px",
                        "&:hover": {
                            backgroundColor: "rgba(59,130,246,0.06)",
                            border: "1px solid #3B82F6",
                        },
                    }}
                >
                    View Results
                </Button>
            </Box>
        </CardShell>
    );
}

/**
 * State 2 — Unattempted
 * Shows orange badge, blue icon, dark filled "Start Test" button.
 */
export function UnattemptedCard({ icon, title, meta, onStartTest }) {
    return (
        <CardShell>
            <CardHeader
                icon={icon}
                title={title}
                locked={false}
                badge={<UnattemptedBadge />}
            />
            <CardMeta meta={meta} />
            <Box sx={{ mt: "auto" }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={onStartTest}
                    disableElevation
                    sx={{
                        backgroundColor: "#111827",
                        color: "#FFFFFF",
                        borderRadius: "6px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        fontFamily: "Mulish, sans-serif",
                        padding: "10px",
                        "&:hover": {
                            backgroundColor: "#1F2937",
                        },
                    }}
                >
                    Start Test
                </Button>
            </Box>
        </CardShell>
    );
}

/**
 * State 3 — Locked
 * Shows gray badge, grayed icon, gray outlined "Unlock Now" button.
 */
export function LockedCard({ icon, title, meta, onUnlock }) {
    return (
        <CardShell>
            <CardHeader
                icon={icon}
                title={title}
                locked={true}
                badge={<LockedBadge />}
            />
            <CardMeta meta={meta} />
            <Box sx={{ mt: "auto" }}>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onUnlock}
                    startIcon={<LockOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
                    sx={{
                        border: "1px solid #D1D5DB",
                        color: "#6B7280",
                        borderRadius: "6px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        fontFamily: "Mulish, sans-serif",
                        padding: "10px",
                        "&:hover": {
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #D1D5DB",
                        },
                    }}
                >
                    Unlock Now
                </Button>
            </Box>
        </CardShell>
    );
}

/**
 * State 4 — Locked Premium (with price + strikethrough)
 * Shows gray badge, grayed icon, price line, gray outlined "Unlock Now" button.
 */
export function LockedPremiumCard({ icon, title, meta, originalPrice, discountedPrice, onUnlock }) {
    return (
        <CardShell>
            <CardHeader
                icon={icon}
                title={title}
                locked={true}
                badge={<LockedBadge />}
            />
            <CardMeta meta={meta} />

            {/* Price line */}
            <Box sx={{ textAlign: "center", mb: "16px" }}>
                <Typography
                    sx={{
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "1rem",
                        color: "#374151",
                        fontWeight: 500,
                    }}
                >
                    Unlock at&nbsp;
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "Mulish, sans-serif",
                            fontSize: "1rem",
                            color: "#9CA3AF",
                            fontWeight: 500,
                            textDecoration: "line-through",
                        }}
                    >
                        ₹{originalPrice}
                    </Typography>
                    &nbsp;
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "Mulish, sans-serif",
                            fontSize: "1rem",
                            color: "#111827",
                            fontWeight: 700,
                        }}
                    >
                        ₹{discountedPrice}
                    </Typography>
                </Typography>
            </Box>

            <Button
                variant="outlined"
                fullWidth
                onClick={onUnlock}
                startIcon={<LockOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
                sx={{
                    border: "1px solid #D1D5DB",
                    color: "#6B7280",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    fontFamily: "Mulish, sans-serif",
                    padding: "10px",
                    "&:hover": {
                        backgroundColor: "#F9FAFB",
                        border: "1px solid #D1D5DB",
                    },
                }}
            >
                Unlock Now
            </Button>
        </CardShell>
    );
}
